/**
 * The Resourced mark as a loading screen — the part of it a document has to do.
 *
 * The cloud itself is not here. It is in `engine.js`, which knows nothing about
 * the DOM, and it normally runs in `loader.worker.js` on its own thread. The
 * reason is the whole point of the split: this screen covers a booting
 * application, which is the busiest the main thread ever gets — sixty requests
 * landing, their responses parsed into stores, and the first render of a whole
 * page — and a frame loop sharing that thread waits behind every one of those
 * tasks. The animation covering the work was being stopped by the work it was
 * covering. On its own thread it simply does not care.
 *
 * What cannot move is what a worker has no access to, and that is what this
 * file keeps: the element and its box, the cursor, the media queries, the
 * fallback mark, and the canvas that gets handed across. Each is measured here
 * and sent over; nothing is measured there.
 *
 * Where a worker or OffscreenCanvas is not available, the same engine is
 * imported and run in this thread instead. It is the same picture and the same
 * code — it is only subject to the jank again.
 *
 * The public shape is unchanged: mount, hold a handle, `finish()` when the app
 * is ready, and `ready` tells you whether you got the cloud or the fallback.
 */
import { MARK_SVG } from './mark.js';
import { rasteriseMark } from './mark-image.js';

/* ── The static fallback ───────────────────────────────────────────────── */

/**
 * What is left when there is no WebGL2, or the context is lost, or the shader
 * will not compile: the mark itself, breathing.
 *
 * This exists because the alternative is an empty box, and an empty box on a
 * loading screen is indistinguishable from a hung application. It is also what
 * `webgl: false` gets, so an app that wants the cheap version can ask for it.
 */
function staticMark(host, ground, reduced) {
  const wrap = document.createElement('div');
  wrap.setAttribute('aria-hidden', 'true');
  wrap.style.cssText =
    'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
    'pointer-events:none;opacity:0;transition:opacity .5s ease;' +
    `color:${ground === 'dark' ? '#ffffff' : '#1d1e24'}`;
  wrap.innerHTML = MARK_SVG.replace('<svg ', '<svg fill="currentColor" ');
  const svg = wrap.firstElementChild;
  // Bounded on both axes so the mark is the same size relative to its box
  // whether that box is a splash screen or a card, and never larger than it.
  svg.style.cssText = 'width:52%;height:auto;max-height:52%;max-width:52%';
  host.appendChild(wrap);

  // Two frames, not one: setting the opacity in the same tick as the insert is
  // not a transition, it is a value.
  requestAnimationFrame(() => requestAnimationFrame(() => { wrap.style.opacity = '1'; }));

  let pulse = null;
  if (!reduced) {
    // The one job left is to say that something is still happening. A slow
    // breath does that without pretending to be a progress bar.
    pulse = svg.animate?.(
      [{ opacity: 0.55 }, { opacity: 1 }, { opacity: 0.55 }],
      { duration: 2400, iterations: Infinity, easing: 'ease-in-out' }
    );
  }
  return {
    fade(ms) { wrap.style.transition = `opacity ${ms}ms ease`; wrap.style.opacity = '0'; },
    stop() { pulse?.cancel(); wrap.remove(); },
  };
}


/* ── Mount ─────────────────────────────────────────────────────────────── */

/** Whether the cloud can be driven from its own thread. */
const CAN_OFFLOAD =
  typeof Worker === 'function' &&
  typeof HTMLCanvasElement !== 'undefined' &&
  typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function';

/**
 * Mounts the loader inside `element` and starts it.
 *
 * Returns synchronously with a handle, so a caller that only wants to show and
 * later dismiss the thing never has to await anything. `ready` is there for
 * callers that care whether they got the cloud or the fallback.
 *
 * @param {Element|string} element  where to mount; a selector is resolved once
 * @returns {{ready: Promise<'cloud'|'static'>, finish: () => Promise<void>,
 *            destroy: () => void, canvas: HTMLCanvasElement|null}}
 */
export function mountLoader(element, options = {}) {
  const host = typeof element === 'string' ? document.querySelector(element) : element;
  if (!host) throw new Error('[resourced-loader] no element to mount in');

  const { ground = 'light', webgl = true, pointer = true, maxDpr = 2 } = options;

  /* The canvas is absolutely positioned inside the host, so the host has to be
     a containing block. Anything already positioned is left alone; only a
     `static` host is nudged, and that is the one thing this touches outside its
     own subtree, which is why the README says so. */
  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* A device with no cursor at all never hands the lens over, so it is settled
     once here rather than being rediscovered every frame. The listener is not
     even attached: a coarse pointer fires `pointermove` on every tap, and a
     lens that jumped to each tap and then wandered back would be the worst of
     both. */
  const coarse = matchMedia('(hover: none), (pointer: coarse)').matches;

  let dead = false;
  let fallback = null;
  let canvas = null;
  let ro = null;
  let io = null;
  let worker = null;
  let inThread = null;
  let following = pointer && !coarse;

  let resolveReady;
  const ready = new Promise((r) => { resolveReady = r; });
  let settled = false;
  const settle = (how) => {
    if (settled) return;
    settled = true;
    resolveReady(how);
  };

  const toStatic = () => {
    if (dead || fallback) return null;
    // The cloud is not coming, so the element it would have drawn into goes.
    canvas?.remove();
    canvas = null;
    fallback = staticMark(host, ground, reduced);
    settle('static');
    return fallback;
  };

  if (!webgl) {
    toStatic();
    return { ready, finish: () => Promise.resolve(fallback?.fade(400)), destroy: () => fallback?.stop(), canvas: null };
  }

  canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none';
  host.appendChild(canvas);

  /* ── What the engine cannot see for itself ── */

  const box = () => {
    const r = host.getBoundingClientRect();
    return { width: Math.max(1, r.width), height: Math.max(1, r.height), dpr: Math.min(maxDpr, devicePixelRatio || 1) };
  };

  const send = (msg) => (worker ? worker.postMessage(msg) : null);

  const pushSize = () => {
    if (dead) return;
    const b = box();
    if (worker) send({ type: 'size', ...b });
    else inThread?.setSize(b.width, b.height, b.dpr);
  };

  const onResize = () => pushSize();

  /* Tracked on the window rather than on the host, which is what the site does
     too. A loader is usually an overlay with `pointer-events: none` on it or
     over it, so events on the element itself cannot be relied on; and a cursor
     that has wandered off the panel still says something about where attention
     is, so it is worth following out of the box rather than dropping.

     Normalised here because only this side can measure the box; undoing the
     lens fit is the engine's half. */
  const onMove = (e) => {
    if (dead || !canvas) return;
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const x = ((e.clientX - r.left) / r.width) * 2 - 1;
    const y = 1 - ((e.clientY - r.top) / r.height) * 2;
    if (worker) send({ type: 'pointer', x, y });
    else inThread?.setPointer(x, y);
  };

  const setVisible = (on) => (worker ? send({ type: 'visible', on }) : inThread?.setVisible(on));

  /* A lost context is not something to recover from. Restoring one means
     rebuilding the atlas and re-uploading every buffer, which is a second of
     work to resume an animation that exists to cover a second of work. The
     preventDefault is still needed — without it the browser will not tell us
     anything more — and then the mark goes in, because a blank rectangle on a
     loading screen is indistinguishable from a hung application. */
  const onLost = (e) => {
    e.preventDefault();
    if (dead) return;
    if (worker) send({ type: 'context-lost' });
    else inThread?.contextLost();
  };
  canvas.addEventListener('webglcontextlost', onLost);

  const listen = () => {
    addEventListener('resize', onResize);
    /* A panel can change size without the window doing anything — a sidebar
       collapsing, a grid reflowing — so the box is watched rather than the
       viewport. The window listener stays as well, because a move between
       displays changes the device pixel ratio without changing the box. */
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(onResize);
      ro.observe(host);
    }
    if (following) addEventListener('pointermove', onMove, { passive: true });

    /* Nothing renders while the loader is off screen. A route transition can
       easily leave one mounted behind a scrolled page, and an invisible cloud
       is still tens of thousands of instances a frame. */
    if (typeof IntersectionObserver === 'function' && canvas) {
      io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
      io.observe(canvas);
    }
  };

  const unlisten = () => {
    removeEventListener('resize', onResize);
    removeEventListener('pointermove', onMove);
    ro?.disconnect();
    io?.disconnect();
    canvas?.removeEventListener('webglcontextlost', onLost);
  };

  /* ── Teardown ── */

  const release = () => {
    if (dead) return;
    dead = true;
    unlisten();
    fallback?.stop();
    worker?.terminate();
    worker = null;
    inThread?.destroy();
    inThread = null;
    canvas?.remove();
    canvas = null;
  };

  /* ── Start ── */

  const env = { reduced, coarse, ...box() };

  let exitPromise = null;
  let finished = null;
  const finish = () => {
    if (dead) return Promise.resolve();
    if (exitPromise) return exitPromise;
    if (fallback && !canvas) {
      fallback.fade(options.exit ?? 1100);
      exitPromise = new Promise((r) => setTimeout(r, (options.exit ?? 1100) + 40));
      return exitPromise;
    }
    if (worker) {
      exitPromise = new Promise((resolve) => {
        finished = () => { release(); resolve(); };
        // The engine answers on its own timer as well as on the animation, but
        // a worker that has died owes nothing — so this side keeps its own.
        setTimeout(() => finished?.(), (options.exit ?? 1100) + 400);
      });
      send({ type: 'finish' });
      return exitPromise;
    }
    exitPromise = (inThread?.finish() ?? Promise.resolve()).then(release);
    return exitPromise;
  };

  if (CAN_OFFLOAD) {
    try {
      worker = new Worker(new URL('./loader.worker.js', import.meta.url), { type: 'module' });
      worker.onmessage = ({ data }) => {
        switch (data.type) {
          case 'ready':
            if (data.counts && canvas) {
              canvas.dataset.pills = String(data.counts.pills);
              canvas.dataset.labelled = String(data.counts.labelled);
            }
            settle(data.how);
            break;
          case 'wants-pointer':
            // The engine decides whether a cursor is worth forwarding; if it is
            // not, the listener is never attached.
            if (!data.wants && following) {
              following = false;
              removeEventListener('pointermove', onMove);
            }
            break;
          case 'static':
            toStatic();
            break;
          case 'context-lost':
            if (canvas) canvas.style.display = 'none';
            toStatic();
            break;
          case 'fade':
            if (canvas) {
              canvas.style.transition = `opacity ${data.ms}ms ease`;
              canvas.style.opacity = '0';
            }
            break;
          case 'finished':
            finished?.();
            break;
          default:
            break;
        }
      };
      worker.onerror = (e) => {
        console.warn('[resourced-loader] worker:', e.message);
        worker?.terminate();
        worker = null;
        toStatic();
      };

      /* The mark is decoded here and sent as pixels: no engine decodes an SVG
         inside a worker, and this is the one piece of setup that cannot cross
         as code. It is a single 512-square, once, before the cloud starts. */
      const offscreen = canvas.transferControlToOffscreen();
      rasteriseMark(512)
        .then((markImage) => {
          if (dead || !worker) return;
          worker.postMessage(
            { type: 'init', canvas: offscreen, options, env: { ...env, markImage } },
            [offscreen]
          );
        })
        .catch((err) => {
          console.warn('[resourced-loader] mark:', err.message);
          worker?.terminate();
          worker = null;
          toStatic();
        });
      listen();
      return { ready, finish, destroy: release, canvas };
    } catch (e) {
      // A transfer that will not happen is not fatal: fall through and run the
      // engine here instead, which is the same picture on a busier thread.
      console.warn('[resourced-loader] falling back to the main thread:', e.message);
      worker?.terminate();
      worker = null;
    }
  }

  import('./engine.js')
    .then(({ createEngine }) => {
      if (dead) return;
      inThread = createEngine(canvas, options, {
        ...env,
        onStatic: () => toStatic(),
        onContextLost: () => { if (canvas) canvas.style.display = 'none'; },
        onFade: (ms) => {
          if (!canvas) return;
          canvas.style.transition = `opacity ${ms}ms ease`;
          canvas.style.opacity = '0';
        },
      });
      if (!inThread.wantsPointer && following) {
        following = false;
        removeEventListener('pointermove', onMove);
      }
      inThread.ready.then((how) => {
        if (canvas && inThread) {
          canvas.dataset.pills = String(inThread.counts.pills);
          canvas.dataset.labelled = String(inThread.counts.labelled);
        }
        settle(how);
      });
      listen();
      pushSize();
    })
    .catch((e) => {
      console.warn('[resourced-loader] engine:', e.message);
      toStatic();
    });

  return { ready, finish, destroy: release, canvas };
}

export default mountLoader;
